import { useEffect, useState } from "react";
import { useLang } from "@/lib/LanguageContext";

const navItems = [
    { id: "home", key: "home" },
    { id: "about", key: "about" },
    { id: "work", key: "work" },
    { id: "offer", key: "offer" },
    { id: "pricing", key: "pricing" },
    { id: "contact", key: "contact" },
];

export default function Navbar() {
    const { lang, setLang, t } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 12);
        handler();
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-ivory/80 backdrop-blur-xl border-b border-hairline"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-screen-2xl px-6 md:px-12 lg:px-20">
                <div className="flex h-28 md:h-36 items-center justify-between">
                    {/* Wordmark / Logo */}
                    <button
                        type="button"
                        data-testid="nav-wordmark"
                        onClick={() => scrollTo("home")}
                        aria-label="Ahmetography — Wall art & Crafts"
                        className="flex items-center"
                    >
                        <img
                            src="https://customer-assets.emergentagent.com/job_color-walls-2/artifacts/h6cx2bzz_Picsart_26-06-24_22-19-59-878.png"
                            alt="Ahmetography — Wall art & Crafts"
                            className="h-24 md:h-32 w-auto rounded-md drop-shadow-md transition-transform duration-300 hover:scale-[1.03]"
                            data-testid="nav-logo-image"
                        />
                    </button>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-10">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                data-testid={`nav-link-${item.id}`}
                                onClick={() => scrollTo(item.id)}
                                className="overline text-navy/80 hover:text-terracotta transition-colors duration-300 link-underline"
                            >
                                {t.nav[item.key]}
                            </button>
                        ))}
                    </nav>

                    {/* Lang switcher */}
                    <div className="flex items-center gap-4">
                        <div
                            className="hidden sm:flex items-center gap-2 overline text-navy/70"
                            data-testid="lang-switcher"
                        >
                            <button
                                type="button"
                                data-testid="lang-en"
                                onClick={() => setLang("en")}
                                className={`transition-colors ${
                                    lang === "en"
                                        ? "text-terracotta"
                                        : "hover:text-navy"
                                }`}
                            >
                                EN
                            </button>
                            <span className="text-navy/30">/</span>
                            <button
                                type="button"
                                data-testid="lang-tr"
                                onClick={() => setLang("tr")}
                                className={`transition-colors ${
                                    lang === "tr"
                                        ? "text-terracotta"
                                        : "hover:text-navy"
                                }`}
                            >
                                TR
                            </button>
                            <span className="text-navy/30">/</span>
                            <button
                                type="button"
                                data-testid="lang-nl"
                                onClick={() => setLang("nl")}
                                className={`transition-colors ${
                                    lang === "nl"
                                        ? "text-terracotta"
                                        : "hover:text-navy"
                                }`}
                            >
                                NL
                            </button>
                        </div>

                        <button
                            type="button"
                            data-testid="mobile-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            aria-label="Toggle menu"
                            className="md:hidden flex flex-col gap-1.5 p-2"
                        >
                            <span
                                className={`block h-px w-6 bg-navy transition-transform ${
                                    open ? "translate-y-[7px] rotate-45" : ""
                                }`}
                            />
                            <span
                                className={`block h-px w-6 bg-navy transition-opacity ${
                                    open ? "opacity-0" : "opacity-100"
                                }`}
                            />
                            <span
                                className={`block h-px w-6 bg-navy transition-transform ${
                                    open ? "-translate-y-[7px] -rotate-45" : ""
                                }`}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden bg-ivory border-t border-hairline"
                >
                    <div className="px-6 py-8 flex flex-col gap-6">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                data-testid={`mobile-nav-${item.id}`}
                                onClick={() => scrollTo(item.id)}
                                className="font-serif text-3xl text-left text-navy"
                            >
                                {t.nav[item.key]}
                            </button>
                        ))}
                        <div className="pt-4 border-t border-hairline flex gap-4 overline">
                            <button
                                type="button"
                                onClick={() => setLang("en")}
                                className={
                                    lang === "en"
                                        ? "text-terracotta"
                                        : "text-navy/70"
                                }
                            >
                                EN
                            </button>
                            <button
                                type="button"
                                onClick={() => setLang("tr")}
                                className={
                                    lang === "tr"
                                        ? "text-terracotta"
                                        : "text-navy/70"
                                }
                            >
                                TR
                            </button>
                            <button
                                type="button"
                                onClick={() => setLang("nl")}
                                className={
                                    lang === "nl"
                                        ? "text-terracotta"
                                        : "text-navy/70"
                                }
                            >
                                NL
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
